import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useQuery } from '@apollo/client';
import { ReviewPoolItem, SimilarStation } from '../types/reviewPool';
import { ExistingSiteOption } from '../mock/reviewPoolMock';
import { LinkStatus } from '../types/pickupSite';
import { GET_COOPERATION_WAREHOUSE_DETAIL } from '../../cooperation-warehouse/graphql/queries/getCooperationWarehouses';
import { GET_ALL_CHANNEL_LITE, allChannelLiteResponse } from '../../../../graphql/queries/getChannelAll';
import { MODAL_HEADER_STYLE, MODAL_FOOTER_STYLE, modalBodyStyle } from '../utils/modalChrome';

/** 关联站点只读确认所需的目标站点详情（子集）。 */
interface CwDetail {
  id: number;
  name?: string;
  status?: number;
  linkStatus?: number;
  warehouseCategory?: string;
  brand?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  contactPersonList?: { name?: string; phone?: string; countryCode?: string | number; inBlacklist?: boolean }[];
  relatedAddressList?: { address1?: string; address2?: string }[];
  standingPickup?: boolean;
  relatedChannelIds?: (number | string)[];
  standingPickupChannelId?: number | string;
  remark?: string;
}

// 仓库类型码 → i18n key（对齐 WarehouseType：1商业仓/2家庭仓/3清关行）
const WH_CATEGORY_KEY: Record<number, string> = { 1: 'commercial', 2: 'home', 3: 'customs' };

interface Props {
  open: boolean;
  item: ReviewPoolItem;
  onClose: () => void;
  onSubmit?: (targetSiteId: number) => void;
}

/** 建联状态文本（对齐原型「状态」下拉选项）。 */
const LINK_STATUS_TEXT: Record<number, string> = {
  1: '待上门',
  2: '上门成功待建联',
  3: '上门失败待建联',
  4: '已建联',
  5: '确认无法建联',
};

/**
 * 关联到已有站点两步向导（相似诊断推荐）。样式严格对齐 PRD 原型（内联 style）。
 * 语义：把当前地址作为关联地址(relate)加到目标站点，后续经该站点聚合创建预约单；
 * 当前地址仍独立存在（区别于物理合并）。历史存量分支另会消解来源站(禁用+清 relate)。
 * Step2「确认站点信息」仅照原型画样式，逻辑为**只读带出目标站点信息**（非编辑）。
 */
const MergeToExistingWizard: React.FC<Props> = ({ open, item, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [targetId, setTargetId] = useState<number | undefined>();
  const [target, setTarget] = useState<ExistingSiteOption | undefined>();
  const [targetDetail, setTargetDetail] = useState<CwDetail | undefined>();
  const [loadDetail] = useLazyQuery<{ getCooperationWarehouseById: CwDetail }>(GET_COOPERATION_WAREHOUSE_DETAIL, {
    fetchPolicy: 'network-only',
  });
  const { data: channelData } = useQuery<allChannelLiteResponse>(GET_ALL_CHANNEL_LITE);
  const channelNameById = useMemo(
    () => new Map((channelData?.getAllChannelsLite ?? []).map((c) => [Number(c.id), c.channelName])),
    [channelData],
  );

  useEffect(() => {
    if (open) {
      setStep(0);
      setTargetId(undefined);
      setTarget(undefined);
      setTargetDetail(undefined);
    }
  }, [open]);

  const sites: SimilarStation[] = item.similarStations || [];
  const tp = (k: string) => t(`pickup.addressReviewPool.${k}`) as string;

  // step1→step2：拉取目标站点详情供只读确认（不改动已有站点，无副作用）
  const handleNext = async () => {
    if (!targetId) return;
    const { data } = await loadDetail({ variables: { id: targetId } });
    setTargetDetail(data?.getCooperationWarehouseById);
    setStep(1);
  };
  const handleOk = () => {
    onSubmit?.(targetId!);
    onClose();
  };

  // 只读字段渲染（样式照原型 roStyle：#f7f6fa 灰底禁用输入框）
  const roStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    border: '1px solid #ececf2',
    borderRadius: 7,
    padding: '8px 10px',
    fontSize: 12.5,
    background: '#f7f6fa',
    color: '#4a4658',
    outline: 'none',
  };
  const labelStyle = (w: number): React.CSSProperties => ({
    fontSize: 12.5,
    color: '#4a4658',
    width: w,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    flex: '0 0 auto',
  });
  const groupTitle: React.CSSProperties = {
    fontSize: 13.5,
    fontWeight: 800,
    color: '#3a3550',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 14,
  };
  const roField = (label: string, value?: React.ReactNode, req = false, labelW = 88) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={labelStyle(labelW)}>
        {label}
        {req && <span style={{ color: '#e11d48' }}>*</span>}
      </span>
      <input readOnly value={value === null || value === undefined || value === '' ? '' : String(value)} style={roStyle} />
    </div>
  );

  const addr2 = targetDetail
    ? [targetDetail.addressLine1, targetDetail.city, targetDetail.state, targetDetail.zipcode].filter(Boolean).join(', ')
    : target?.address || '';
  // 联系人：按原型拆成「联系人 | +区号 | 电话号码」独立框展示（非拼接串），至少渲染一行
  const contactRows = targetDetail?.contactPersonList?.length ? targetDetail.contactPersonList : [{}];
  const docksText = targetDetail?.relatedAddressList?.length
    ? targetDetail.relatedAddressList
        .map((r) => r.address1)
        .filter(Boolean)
        .join('；')
    : '';
  // 渠道 id 解析成客户名：Long 出参为字符串，用 Number 归一后查名（否则 map 命中失败退化成 #id）
  const channelsText = targetDetail?.relatedChannelIds?.length
    ? targetDetail.relatedChannelIds.map((id) => channelNameById.get(Number(id)) ?? `#${id}`).join('；')
    : '';
  const standingClientText =
    targetDetail?.standingPickupChannelId != null
      ? channelNameById.get(Number(targetDetail.standingPickupChannelId)) ?? `#${targetDetail.standingPickupChannelId}`
      : '';
  const warehouseCategoryText =
    targetDetail?.warehouseCategory != null
      ? (t(
          `pickup.addressReviewPool.warehouseType.${WH_CATEGORY_KEY[Number(targetDetail.warehouseCategory)] ?? ''}`,
          String(targetDetail.warehouseCategory),
        ) as string)
      : '';
  const statusText = targetDetail?.linkStatus ? LINK_STATUS_TEXT[targetDetail.linkStatus] ?? '-' : '-';

  const circle = (active: boolean): React.CSSProperties => ({
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: active ? '#7c4bf0' : '#b7b2c4',
    color: '#fff',
    fontSize: 15,
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 34px',
  });
  const greyBtn: React.CSSProperties = {
    border: '1px solid #b7b2c4',
    background: '#8a8698',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    padding: '9px 24px',
    borderRadius: 9,
    cursor: 'pointer',
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
      {step === 0 ? (
        <>
          <button onClick={onClose} style={greyBtn}>
            {t('common.cancel', '取消') as string}
          </button>
          <button
            onClick={handleNext}
            disabled={!targetId}
            style={{
              border: 'none',
              background: targetId ? '#6d28d9' : '#c9bff0',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              padding: '9px 28px',
              borderRadius: 9,
              cursor: targetId ? 'pointer' : 'not-allowed',
            }}
          >
            {tp('wizard.next')}
          </button>
        </>
      ) : (
        <>
          <button onClick={() => setStep(0)} style={greyBtn}>
            {tp('wizard.prev')}
          </button>
          <button
            onClick={handleOk}
            style={{
              border: 'none',
              background: '#6d28d9',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              padding: '9px 28px',
              borderRadius: 9,
              cursor: 'pointer',
            }}
          >
            {t('common.confirm', '确定') as string}
          </button>
        </>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      width={960}
      onCancel={onClose}
      footer={footer}
      // header/footer 固定并各带分隔线，滚动只发生在两线之间（对齐 PRD 原型）
      styles={{
        header: MODAL_HEADER_STYLE,
        body: modalBodyStyle('72vh'),
        footer: MODAL_FOOTER_STYLE,
      }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 5, height: 22, background: '#7c4bf0', borderRadius: 3, display: 'inline-block' }} />
          <span style={{ fontSize: 18, fontWeight: 800 }}>{tp('mergeWizard.title')}</span>
        </div>
      }
    >
      {/* 副标题（原型：紫色小字 + 下边框） */}
      <div style={{ fontSize: 12, color: '#6d28d9', paddingBottom: 14, borderBottom: '1px solid #efeaf7' }}>
        {tp('mergeWizard.pickHint')}
      </div>

      {/* stepper（原型：34px 圆圈 + 连接线） */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '20px 0' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <span style={circle(true)}>1</span>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: '#3a3550' }}>{tp('mergeWizard.step1')}</span>
        </div>
        <span style={{ width: 120, height: 2, background: '#2a2636', marginBottom: 24 }} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <span style={circle(step === 1)}>2</span>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: '#3a3550' }}>{tp('mergeWizard.step2')}</span>
        </div>
      </div>

      <div style={{ borderTop: '1px solid #efeaf7', paddingTop: 6 }}>
        {step === 0 ? (
          /* ===== STEP1：当前地址 + 相同地址站点表格（照原型） ===== */
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#3a3550', margin: '16px 0 8px' }}>
              {tp('mergeWizard.currentAddressLabel')}
            </div>
            <div
              style={{
                border: '1px solid #d9d5e6',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                textAlign: 'center',
                color: '#2a2636',
                marginBottom: 22,
              }}
            >
              {item.displayAddress}
            </div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#3a3550', marginBottom: 4 }}>
              {tp('mergeWizard.sameAddressLabel')}
            </div>
            <div style={{ fontSize: 11.5, color: '#8a8698', marginBottom: 10 }}>{tp('mergeWizard.sameAddressHint')}</div>
            <div style={{ border: '1px solid #e6e2f0', borderRadius: 10, overflow: 'hidden' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 1fr 1fr 2.4fr',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#6b6778',
                  background: '#faf8ff',
                  padding: '10px 14px',
                }}
              >
                <span />
                <span>{tp('mergeWizard.colSiteId')}</span>
                <span>{tp('mergeWizard.colName')}</span>
                <span>{tp('mergeWizard.colAddress')}</span>
              </div>
              {sites.map((s) => {
                // 置灰不可选，并当场说明原因——与 AB 向导同口径（见 AbMappingWizard 的 isUnreachableSite）。
                // 服务端 PickupReviewPoolProcessService#resolveAsMergeToExisting 本就会拒绝，
                // 但此前前端看不出来，运营选完提交才吃一个失败，然后反复重试并报障。
                const disabled = s.linkStatus === LinkStatus.Unreachable;
                return (
                  <div
                    key={s.id}
                    onClick={
                      disabled
                        ? undefined
                        : () => {
                            setTargetId(s.id);
                            setTarget({ id: s.id, name: s.name || '', address: s.address, contact: s.contact });
                          }
                    }
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '40px 1fr 1fr 2.4fr',
                      fontSize: 12.5,
                      color: disabled ? '#a8a4b5' : '#2a2636',
                      padding: '10px 14px',
                      borderTop: '1px solid #f0edf6',
                      alignItems: 'center',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      background: disabled ? '#fafafa' : undefined,
                    }}
                  >
                    <input
                      type="checkbox"
                      readOnly
                      disabled={disabled}
                      checked={targetId === s.id}
                      style={{
                        width: 15,
                        height: 15,
                        accentColor: '#6d28d9',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                      }}
                    />
                    <span style={{ fontWeight: 600 }}>{s.id}</span>
                    <span>{s.name || '-'}</span>
                    <span>
                      {s.address}
                      {disabled && (
                        <div style={{ fontSize: 11.5, color: '#b45309', marginTop: 2 }}>
                          ⚠️ {tp('mergeWizard.unreachableSiteHint')}
                        </div>
                      )}
                    </span>
                  </div>
                );
              })}
              {sites.length === 0 && (
                <div style={{ padding: '16px 14px', fontSize: 12.5, color: '#928da0', textAlign: 'center' }}>—</div>
              )}
            </div>
          </div>
        ) : (
          /* ===== STEP2：照原型「确认站点信息」样式，只读带出目标站点 ===== */
          <div style={{ paddingTop: 8, color: '#1f2430' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <span style={{ fontSize: 12.5, color: '#6b6778', fontWeight: 700, width: 96, textAlign: 'right' }}>ID：</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#2a2636' }}>{targetDetail?.id ?? target?.id}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 40px', marginBottom: 22 }}>
              {roField(tp('siteForm.name'), targetDetail?.name, true)}
              {roField(tp('siteForm.status'), statusText, true)}
              {roField(tp('siteForm.warehouseType'), warehouseCategoryText, true)}
              {roField(tp('siteForm.brand'), targetDetail?.brand)}
            </div>

            <div style={groupTitle}>🏠 {tp('mergeWizard.sectionAddressContact')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 22 }}>
              {roField(tp('siteForm.pickupAddress'), addr2, true, 104)}
              {/* 联系人：照原型「联系人 | +区号 | 电话号码」独立框，每个联系人一行 */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ ...labelStyle(104), marginTop: 8 }}>
                  {tp('siteForm.contactPerson')}
                  <span style={{ color: '#e11d48' }}>*</span>
                </span>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {contactRows.map((c, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8 }}>
                      <input readOnly value={c.name ?? ''} style={{ ...roStyle, flex: 2 }} />
                      <input
                        readOnly
                        value={c.countryCode != null && `${c.countryCode}` !== '' ? `+${`${c.countryCode}`.replace(/^\+/, '')}` : ''}
                        style={{ ...roStyle, flex: '0 0 70px' }}
                      />
                      <input readOnly value={c.phone ?? ''} style={{ ...roStyle, flex: 2 }} />
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ ...labelStyle(104), marginTop: 2 }}>{tp('mergeWizard.relatedDocks')}</span>
                <div style={{ flex: 1, fontSize: 12, color: '#5b5768', lineHeight: 1.9 }}>{docksText || '-'}</div>
              </div>
            </div>

            <div style={groupTitle}>📋 {tp('mergeWizard.sectionPickupInfo')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 40px', marginBottom: 14 }}>
              {roField(
                tp('siteForm.standingPickup'),
                targetDetail?.standingPickup ? (t('common.yes', '是') as string) : (t('common.no', '否') as string),
                true,
                120,
              )}
              {roField(tp('siteForm.standingClient'), standingClientText, false, 120)}
              {roField(tp('siteForm.relatedClient'), channelsText, true, 120)}
            </div>
            {roField(tp('siteForm.remark'), targetDetail?.remark, true, 104)}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MergeToExistingWizard;
